import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  forwardRef,
  inject,
  Injector,
  input,
  Input,
  OnInit,
  viewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteModule,
} from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import {
  MatFormFieldAppearance,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest, map, Observable, startWith, tap } from 'rxjs';
import { ErrorFieldComponent } from '../error-field/error-field.component';

/**
 * A single availability slot (UTC time strings, e.g. "08:00:00") for a macchinario.
 */
export interface OrarioAttivita {
  ora_inizio: string;
  ora_fine: string;
}

export interface Item {
  id: number | string;
  name: string;
  nome?: string;
  visible?: boolean;
  checked?: boolean;
  disabled?: boolean;
  icon?: string;
  iconFontSet?: string;
  tipo?: string;
  icd10?: string;
  selected?: boolean;
  lavori?: string[];
  type?: string;
  minorenne?: boolean;
  index?: number;
  nomePaese?: string;
  /**
   * Raw, not-yet-mapped macchinario payload for this item; shape depends on
   * the API endpoint that produced it (see `mapMacchinariToItems`).
   */
  macchinari?: unknown[];
  durataSlotMacchinario?: number;
  orariAttivita?: OrarioAttivita[];
  /**
   * Optional secondary icon shown next to `icon` in the option row, with an
   * accessible label/tooltip. Generic on purpose: what it flags (minorenne,
   * ambulatorio complesso, ecc.) is entirely up to the consumer populating it.
   */
  extraIcon?: string;
  extraIconLabel?: string;
  /** True if this item represents an ambulatorio/struttura rather than a sanitario person. */
  isAmbulatorio?: boolean;
  /** True if the current user acts as segretario for this item. */
  isSegretario?: boolean;
}
/**
 * @example
 * <siat-select-field
 * [formGroup]="formGroup"
 * controlName="controlName"
 * [items]="[{id: 1, name: 'item1'}, {id: 2, name: 'item2'}]"
 * id="id"
 * placeholder="placeholder"
 * label="label"
 * showSearch="true"
 * showStatus="true"
 * showError="false"
 * > </siat-select-field>
 */

@UntilDestroy()
@Component({
  selector: 'siat-select-field',
  templateUrl: './select-field.component.html',
  styleUrls: ['./select-field.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ErrorFieldComponent,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    TranslatePipe,
    MatIcon,
    MatIconButton,
    MatTooltip,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectFieldComponent),
      multi: true,
    },
  ],
})
export class SelectFieldComponent
  implements OnInit, AfterViewInit, ControlValueAccessor
{
  /**
   * The form group associated with the input field.
   */
  formGroup = input<FormGroup>();

  /**
   * The control name associated with the input field.
   */

  controlName = input<string>('');

  /**
   * A boolean it tells if label need to show.
   */
  showLabel = input<boolean>(true);

  /**
   * Opt-in: also renders the selected option's icon as a `matPrefix` once closed; off by default so consumers reusing `icon` for unrelated meanings (e.g. select-tipologica-field's 'home' for "ambito") are unaffected.
   */
  showSelectedIcon = input<boolean>(false);

  /**
   * Accessible name for the input, set as `aria-label` (e.g. when `showLabel`
   * is off and the visible label alone wouldn't be announced).
   */
  ariaLabel = input<string>('');

  /**
   * Opt-in: shows a `matSuffix` clear icon (before the dropdown arrow) once
   * the field has a value, resetting just the value — not to be confused
   * with a consumer-side "remove this field" action. Off by default.
   */
  showClearButton = input<boolean>(false);

  /**
   * The id attribute of the input field.
   */

  id = input<string>('');

  /**
   * Th placeholder of the input field.
   */
  placeholder = input<string>('');

  /**
   * The input label.
   */
  label = input<string>('');

  /**
   * The input "name" attribute value.
   */
  name? = input<string>('');

  /**
   * Items input.
   */
  items = input<Item[]>([]);

  /**
   * The style class of the field.
   */
  @Input()
  fieldClass: string;

  /**
   * The appearance of the form field.
   */
  appearance = input<MatFormFieldAppearance>('outline');

  /**
   * The disabled ids.
   */
  disabledIds = input<number[] | string[]>();

  /**
   * A boolean it tells if that component are dynamic field.
   * @see DynamicFormViewerComponent
   */
  areDynamicField = input<boolean>(false);

  /**
   * The empty message if no items are available.
   */
  emptyMessage = input<string>('');

  // Internal properties.
  private autoComplete = viewChild<MatAutocomplete>('auto');
  private injector = inject(Injector);
  private internalControl = new FormControl<Item | null>(null);
  public filteredOptions$: Observable<Item[]>;

  /**
   * @returns { FormControl }
   */
  get control(): FormControl<Item | string | null> {
    if (this.formGroup() && this.controlName()) {
      return this.formGroup()?.controls[this.controlName()] as FormControl<
        Item | string | null
      >;
    }
    return this.internalControl;
  }

  /**
   * @returns { boolean }
   */
  get disabled(): boolean {
    return this.control.disabled;
  }

  /**
   * The constructor
   */
  constructor() {
    this.internalControl.valueChanges.subscribe((value) => {
      this.onChange(value as Item);
      this.onTouched();
    });
  }

  /**
   * @inheritdoc
   */
  ngOnInit(): void {
    if (this.areDynamicField()) {
      // Initialize options listener for dynamic form
      const formGroup = this.formGroup();
      const haveOptions = formGroup.get('options') as FormArray;
      const arePriorityField = formGroup.get('type')?.value === 'priority';

      // If the field has options, listen to value changes
      if (haveOptions) {
        this.listenToValueChanges();
      }
      // If the field is priority field, listen to value changes to update selected option
      if (arePriorityField) {
        this.listenToPriorityField();
      }
    }

    const value$ = this.control.valueChanges.pipe(
      startWith(this.control.value),
    );
    const items$ = toObservable(this.items, { injector: this.injector });
    const combined$ = combineLatest([value$, items$]);

    combined$.subscribe(([currentValue, items]) => {
      if (typeof currentValue === 'object' || !currentValue) {
        return;
      }
      if (!items || items.length === 0) {
        return;
      }

      const matchingItem = items.find(
        (item) =>
          item?.id == currentValue ||
          (typeof currentValue === 'string' &&
            item?.name.toLowerCase() === currentValue.toLowerCase()),
      );

      if (matchingItem) {
        this.control.setValue(matchingItem, { emitEvent: true });
      }
    });

    this.filteredOptions$ = combined$.pipe(
      map(([value, items]) => {
        if (!items) return [];
        const filterValue = typeof value === 'string' ? value : '';
        return items?.filter((option) =>
          option?.name?.toLowerCase().includes(filterValue.toLowerCase()),
        );
      }),
    );
  }

  /**
   * Display function
   */
  displayFn(node: Item | string): string {
    return (node as Item)?.name ?? (node as string);
  }

  /**
   * Resets the control value, used by the opt-in `showClearButton` icon.
   */
  clearValue(event: Event): void {
    event.stopPropagation();
    this.control.setValue(null);
    this.control.markAsTouched();
  }

  /**
   * The selected item's icon (e.g. 'home', 'assignment_ind', 'account_circle'), shown as a `matPrefix` once closed.
   * `extraIcon` takes precedence over `icon`, mirroring the option row's own precedence; falls back to 'person'.
   */
  get selectedIcon(): string | undefined {
    if (!this.showSelectedIcon()) return undefined;
    const value = this.control.value;
    if (!value || typeof value !== 'object') return undefined;
    const item = value as Item;
    return item.extraIcon || item.icon || 'person';
  }

  /**
   * @returns { string } the fontSet for `selectedIcon`.
   */
  get selectedIconFontSet(): string | undefined {
    const value = this.control.value;
    if (!value || typeof value !== 'object') return undefined;
    return (value as Item).iconFontSet;
  }

  /**
   * @inheritdoc
   */
  ngAfterViewInit(): void {
    this.autoComplete()
      ?.closed.pipe(untilDestroyed(this))
      .subscribe(() => {
        if (typeof this.control.value === 'string') {
          const currentText = this.control.value;
          const matchingItem = this.items()?.find(
            (item) => item?.name.toLowerCase() === currentText.toLowerCase(),
          );
          if (matchingItem) {
            this.control.setValue(matchingItem);
          } else {
            this.control.setValue(null);
          }
          this.control.markAsTouched();
        }
      });
  }

  /**
   * @override
   */
  onChange: (value: Item | null) => void = () => {};

  /**
   * @override
   */
  onTouched: () => void = () => {};

  /**
   * @override
   */
  registerOnChange(changeCallback: (value: Item | null) => void): void {
    this.onChange = changeCallback;
  }

  /**
   * @override
   */
  registerOnTouched(touchedCallback: () => void) {
    this.onTouched = touchedCallback;
  }

  /**
   * @override
   */
  writeValue(value: Item | null): void {
    if (!this.formGroup() || !this.controlName()) {
      this.internalControl.setValue(value, { emitEvent: false });
    } else {
      this.control.setValue(value, { emitEvent: false });
    }
  }

  /**
   * Set disabled state.
   */
  setDisabledState(isDisabled: boolean): void {
    if (!this.formGroup() || !this.controlName()) {
      if (isDisabled) {
        this.internalControl.disable();
      } else {
        this.internalControl.enable();
      }
    } else {
      if (isDisabled) {
        this.control.disable();
      } else {
        this.control.enable();
      }
    }
  }

  /**
   * @returns { boolean } it some option item are disabled.
   */
  isOptionDisabled(option: Item): boolean {
    if (!option) return false;
    const idToCheck = option?.id;
    const listOfDisabledIds = this.disabledIds();
    if (!listOfDisabledIds || listOfDisabledIds.length === 0) return false;
    return listOfDisabledIds.some((disabledId) => disabledId == idToCheck);
  }

  /**
   * Listen to value changes and
   * update selected options for dynamic form
   * @see DynamicFormViewerComponent
   */
  listenToValueChanges() {
    this.control.valueChanges
      .pipe(
        untilDestroyed(this),
        tap((item: Item) => {
          const optionsFormArray = this.formGroup().get('options') as FormArray;

          optionsFormArray?.controls?.forEach((optionControl) => {
            const optionFormGroup = optionControl as FormGroup;
            const currentId = optionFormGroup.get('id')?.value;
            const selectedControl = optionFormGroup.get(
              'selected',
            ) as FormControl;

            const isSelected = currentId === item?.id;
            selectedControl.setValue(isSelected, { emitEvent: false });
          });
        }),
      )
      .subscribe();
  }

  /**
   * Listen to value changes and
   * update selected options for dynamic form
   * @see DynamicFormViewerComponent
   */
  private listenToPriorityField(): void {
    this.control.valueChanges
      .pipe(
        untilDestroyed(this),
        tap((item: Item) => {
          this.control.setValue(item?.name, { emitEvent: false });
        }),
      )
      .subscribe();
  }
}
